import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Search, X } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuditLogs } from '@/api/audit';
import { Activity } from 'lucide-react';
import type { AuditLog } from '@/types/api';

function targetLabel(entry: AuditLog): { name: string | null; location: string | null } {
  const meta = entry.metadata ?? {};
  const name = typeof meta.name === 'string' ? meta.name : null;
  const folderName = typeof meta.folder_name === 'string' ? meta.folder_name : null;
  const title = typeof meta.title === 'string' ? meta.title : null;
  const to = entry.action === 'file.rename' && typeof meta.to === 'string' ? meta.to : null;
  return { name: name ?? to ?? title, location: folderName };
}

const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.sso.login': 'Signed in via SSO',
  'file.upload': 'Uploaded file',
  'file.download': 'Downloaded file',
  'file.delete': 'Deleted file',
  'file.rename': 'Renamed file',
  'folder.create': 'Created folder',
  'folder.delete': 'Deleted folder',
  'invite.create': 'Sent invite',
  'user.update': 'Updated user',
  'user.password.change': 'Changed password',
};

const PAGE_LIMIT = 200;

export function WorkspaceActivityPage() {
  usePageTitle('Activity');
  const { orgId = '' } = useParams<{ orgId: string }>();
  const { user } = useAuth();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const effectiveOrgId = orgId || activeOrgId || user?.organization_id || '';
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AuditLog | null>(null);

  useEffect(() => { setSearch(''); setSelected(null); }, [effectiveOrgId]);

  const { data, isLoading } = useAuditLogs({
    org_id: effectiveOrgId || null,
    limit: PAGE_LIMIT,
    offset: 0,
  });

  const allItems = data?.items ?? [];

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((e) => {
      const actor = (e.actor_name || e.actor_email || '').toLowerCase();
      const action = (ACTION_LABEL[e.action] ?? e.action).toLowerCase();
      const ip = (e.source_ip || '').toLowerCase();
      return actor.includes(q) || action.includes(q) || ip.includes(q);
    });
  }, [allItems, search]);

  return (
    <AppShell crumbs={[{ label: 'Activity' }]}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-[#12122C]">Activity log</h1>
            <p className="text-xs text-[#676687] mt-0.5">All actions in your workspace.</p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E3E3F1] rounded-lg bg-white w-56 shrink-0">
            <Search size={14} className="text-[#BABACE] shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="User, action, or IP…"
              className="flex-1 min-w-0 text-sm bg-transparent outline-none text-[#12122C] placeholder:text-[#BABACE]"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="text-[#BABACE] hover:text-[#676687]">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          {/* Left: entry list */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">
              {isLoading ? (
                <div className="flex justify-center py-12"><Spinner size={24} /></div>
              ) : allItems.length === 0 ? (
                <EmptyState
                  title="No activity yet"
                  description="Actions in your workspace will appear here."
                  icon={<Activity size={24} />}
                />
              ) : (
                <div className="overflow-y-auto max-h-[calc(100vh-220px)] custom-scrollbar divide-y divide-[#F2F2F8]">
                  {items.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-[#676687]">
                      No results for <strong>"{search}"</strong>
                    </div>
                  ) : (
                    items.map((entry) => {
                      const isActive = selected?.id === entry.id;
                      return (
                        <div
                          key={entry.id}
                          onClick={() => setSelected(isActive ? null : entry)}
                          className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${isActive ? 'bg-[#EFF0FF]' : 'hover:bg-[#F9F9FC]'}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className={`text-sm font-medium ${isActive ? 'text-[#0410BD]' : 'text-[#12122C]'}`}>
                                {entry.actor_name || entry.actor_email || 'System'}
                              </span>
                              <span className="text-sm text-[#676687]">
                                {ACTION_LABEL[entry.action] ?? entry.action}
                              </span>
                              {entry.target_id && (() => {
                                const lbl = targetLabel(entry);
                                return (
                                  <span className="text-xs text-[#BABACE] truncate max-w-[200px]">
                                    {lbl.name ?? entry.target_id}
                                    {lbl.name && lbl.location && ` · ${lbl.location}`}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="text-xs text-[#BABACE] mt-0.5">
                              {entry.created_at ? format(new Date(entry.created_at), 'PP p') : '—'}
                              {entry.source_ip && ` · ${entry.source_ip}`}
                            </div>
                          </div>
                          {entry.outcome && entry.outcome !== 'success' && (
                            <span className="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">
                              {entry.outcome}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Right: detail panel */}
          <div className="lg:sticky lg:top-4">
            {selected ? (
              <Card>
                <div className="flex items-start justify-between mb-4">
                  <span className="text-sm font-semibold text-slate-800">Event detail</span>
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={14} />
                  </button>
                </div>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Action</dt>
                    <dd className="text-slate-800">{ACTION_LABEL[selected.action] ?? selected.action}</dd>
                    <dd className="text-xs text-slate-400 font-mono mt-0.5">{selected.action}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Who</dt>
                    <dd className="text-slate-800">{selected.actor_name ?? 'System'}</dd>
                    {selected.actor_email && (
                      <dd className="text-xs text-slate-400">{selected.actor_email}</dd>
                    )}
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">When</dt>
                    <dd className="text-slate-800">{selected.created_at ? format(new Date(selected.created_at), 'PPpp') : '—'}</dd>
                  </div>
                  {selected.source_ip && (
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">IP address</dt>
                      <dd className="text-slate-800 font-mono text-xs">{selected.source_ip}</dd>
                    </div>
                  )}
                  {selected.target_id && (() => {
                    const lbl = targetLabel(selected);
                    return (
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Target</dt>
                        {lbl.name && <dd className="text-slate-800">{lbl.name}</dd>}
                        {lbl.location && <dd className="text-xs text-slate-500 mt-0.5">Folder: {lbl.location}</dd>}
                        <dd className="text-xs text-slate-400 font-mono break-all mt-0.5">{selected.target_id}</dd>
                        {selected.target_type && (
                          <dd className="text-xs text-slate-400 capitalize mt-0.5">{selected.target_type}</dd>
                        )}
                      </div>
                    );
                  })()}
                  {selected.outcome && (
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Outcome</dt>
                      <dd className={`text-sm capitalize ${selected.outcome === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {selected.outcome}
                      </dd>
                    </div>
                  )}
                  {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Metadata</dt>
                      <dd>
                        <div className="rounded border border-[#E3E3F1] divide-y divide-[#E3E3F1]">
                          {Object.entries(selected.metadata).map(([k, v]) => (
                            <div key={k} className="flex items-start gap-2 px-2 py-1.5">
                              <span className="text-xs text-slate-500 w-24 shrink-0 capitalize">{k.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-800 break-all">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </dd>
                    </div>
                  )}
                </dl>
              </Card>
            ) : (
              <div className="border border-dashed border-[#E3E3F1] rounded-lg p-6 text-center text-sm text-slate-400">
                Click an entry to see details
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
